import type { AxiosResponse } from 'axios';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { getType } from 'typesafe-actions';
import type { ActionType } from 'typesafe-actions';

import type { LeadResponse, LeadSummary } from '@evenfinancial/lead-client';

import { client } from '@portal/api-client/src/client';

import {
  getLeadByUuidAction,
  getLeadSummariesAction,
  getLeadSummaryByUuidAction,
  getLeadsAction,
  leadSummariesUtilStateChangeAction,
  leadsUtilStateChangeAction,
} from './actions';

export function* getLeads() {
  try {
    yield put(leadsUtilStateChangeAction({ error: false, loading: true }));
    const { data }: AxiosResponse<LeadResponse[]> = yield call(
      [client, 'get'],
      '/leads',
      {
        params: {
          limit: 1500,
        },
      }
    );

    yield put(getLeadsAction.success(data));
    yield put(leadsUtilStateChangeAction({ error: false, loading: false }));
  } catch (err: any) {
    yield put(getLeadsAction.failure(err));
    yield put(leadsUtilStateChangeAction({ error: true, loading: false }));
  }
}

export function* getLeadByUuid({
  payload: { uuid },
}: ActionType<typeof getLeadByUuidAction.request>) {
  try {
    yield put(leadsUtilStateChangeAction({ error: false, loading: true }));
    const { data }: AxiosResponse<LeadResponse[]> = yield call(
      [client, 'get'],
      '/leads',
      {
        params: {
          limit: 1,
          uuid,
        },
      }
    );

    const lead = data?.[0];

    if (!lead) {
      throw new Error(`No lead with uuid: ${uuid}`);
    }

    yield put(getLeadByUuidAction.success({ lead, uuid }));

    yield put(leadsUtilStateChangeAction({ error: false, loading: false }));
  } catch (e) {
    yield put(getLeadByUuidAction.failure(e as Error));
    yield put(leadsUtilStateChangeAction({ error: true, loading: false }));
  }
}

export function* getLeadSummaries() {
  try {
    yield put(
      leadSummariesUtilStateChangeAction({
        error: false,
        loading: true,
      })
    );
    const { data }: AxiosResponse<LeadSummary[]> = yield call(
      [client, 'get'],
      '/leads/summaries',
      {
        params: {
          limit: 1500,
        },
      }
    );

    yield put(getLeadSummariesAction.success(data));
    yield put(
      leadSummariesUtilStateChangeAction({
        error: false,
        loading: false,
      })
    );
  } catch (e) {
    yield put(getLeadSummariesAction.failure(e as Error));
    yield put(
      leadSummariesUtilStateChangeAction({
        error: true,
        loading: false,
      })
    );
  }
}

export function* getLeadSummaryByUuid({
  payload: { uuid },
}: ActionType<typeof getLeadSummaryByUuidAction.request>) {
  try {
    yield put(
      leadSummariesUtilStateChangeAction({
        error: false,
        loading: true,
      })
    );
    const { data }: AxiosResponse<LeadSummary> = yield call(
      [client, 'get'],
      `/leads/summaries/${uuid}`
    );

    yield put(getLeadSummaryByUuidAction.success(data));
    yield put(
      leadSummariesUtilStateChangeAction({
        error: false,
        loading: false,
      })
    );
  } catch (e) {
    yield put(getLeadSummaryByUuidAction.failure(e as Error));
    yield put(
      leadSummariesUtilStateChangeAction({
        error: true,
        loading: false,
      })
    );
  }
}

export function* leadSaga() {
  yield takeLatest(getType(getLeadsAction.request), getLeads);
  yield takeEvery(getType(getLeadByUuidAction.request), getLeadByUuid);
  yield takeLatest(getType(getLeadSummariesAction.request), getLeadSummaries);
  yield takeEvery(
    getType(getLeadSummaryByUuidAction.request),
    getLeadSummaryByUuid
  );
}
